import React, { useState } from "react";
import { ethers } from "ethers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const chains = [
  {
    id: 6278,
    name: "Rails Network",
    symbol: "STEAMX",
    rpcUrl: "https://mainnet.steamexchange.io",
    image: "https://github.com/divechan/brandkit/blob/main/SVGs/new-logos/mainnet.png?raw=true",
    priceEndpoint: "https://api.geckoterminal.com/api/v2/networks/rails-network/tokens/0x0000000000000000000000000000000000627800?include=price",
  },
  {
    id: 1,
    name: "Ethereum",
    symbol: "ETH",
    rpcUrl: "https://eth-pokt.nodies.app",
    image: "https://raw.githubusercontent.com/divechan/brandkit/main/eth.png",
    chainKey: "ETH",
    tokens: [{ address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", symbol: "USDC", price: 1 }],
    priceEndpoint: "https://api.geckoterminal.com/api/v2/networks/eth/tokens/0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2?include=price",
  },
  {
    id: 56,
    name: "BSC",
    symbol: "BNB",
    rpcUrl: "https://bsc-pokt.nodies.app",
    chainKey: "BSC",
    image: "https://raw.githubusercontent.com/divechan/brandkit/main/bsc.png",
    tokens: [{ address: "0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56", symbol: "BUSD", price: 1 }],
    priceEndpoint: "https://api.geckoterminal.com/api/v2/networks/bsc/tokens/0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c?include=price",
  },
  {
    id: 8453,
    name: "Base",
    symbol: "ETH",
    rpcUrl: "https://base-rpc.publicnode.com",
    chainKey: "BASE",
    image: "https://raw.githubusercontent.com/divechan/brandkit/main/base.png",
    tokens: [{ address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", symbol: "USDC", price: 1 }],
    priceEndpoint: "https://api.geckoterminal.com/api/v2/networks/base/tokens/0x4200000000000000000000000000000000000006?include=price",
  },
];


const WalletConnectButton = ({
  walletAddress = "", // Safe default value
  setWalletAddress = () => {}, // Safe no-op fallback
  isExpanded = false, // Default modal state
  toggleWalletModal = () => {}, // Safe no-op fallback
  zIndex = "z-30", // Default z-index
}) => {
  const [networkBalances, setNetworkBalances] = useState([]);
  const moralisApiKey =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6ImM0ZTQxMDVjLTNhNTMtNDYzMy1iZWU4LTJiZDAwMGIzN2I5MiIsIm9yZ0lkIjoiMzk1ODgyIiwidXNlcklkIjoiNDA2Nzk2IiwidHlwZUlkIjoiZThhNGJlN2UtZGE3NC00MjAyLTk5MWQtMWFmYzVhOGE4NThmIiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3MTgwNTEwMTIsImV4cCI6NDg3MzgxMTAxMn0.ZC-p-yhVGtvKVmSuC6tFvTPaiR6SYtulSwuknQu79bo";

  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error("MetaMask is not installed. Please install it to use this feature.");
      return;
    }

    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const account = accounts[0];
      if (!account) throw new Error("No accounts returned from wallet");

      setWalletAddress(account);
      await fetchAllNetworkBalances(account);
    
      if (typeof toggleWalletModal === "function") toggleWalletModal();
    } catch (error) {
      console.error("Error connecting wallet:", error);
      toast.error("Failed to connect wallet. Please try again.");
    }
  };
  

  const fetchPriceForRails = async (chain) => {
    try {
      const response = await fetch(chain.priceEndpoint);
      const data = await response.json();
      return parseFloat(data.data.attributes.price_usd) || 0;
    } catch (error) {
      console.error(`Error fetching price for ${chain.name}:`, error);
      return 0;
    }
  };

  const fetchTokenPrice = async (chain, tokenAddress) => {
    try {
      const response = await fetch(
        `https://api.geckoterminal.com/api/v2/networks/${chain}/tokens/${tokenAddress}`,
        { method: "GET" }
      );
  
      if (!response.ok) {
        throw new Error(`Failed to fetch price for token: ${tokenAddress}`);
      }
  
      const data = await response.json();
      const price = data?.data?.attributes?.price_usd || 0;
  
      if (!price) {
        console.warn(`Price not found for token: ${tokenAddress}`);
      }
  
      return price;
    } catch (error) {
      console.error(`Error fetching price for token ${tokenAddress}:`, error);
      return 0;
    }
  };
 
  const fetchAllNetworkBalances = async (address) => {
    const balances = await Promise.all(
      chains.map(async (chain) => {
        if (chain.id === 6278) {
          // Rails-specific logic
          try {
            const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
            const nativeBalance = await provider.getBalance(address);
            const nativeValue = parseFloat(ethers.formatEther(nativeBalance)) || 0;
  
            // Fetch native price using Rails-specific price endpoint
            const nativePrice = await fetchPriceForRails(chain);
            const nativeTotal = nativeValue * nativePrice;
  
            console.log(
              `Rails Native Balance: ${nativeValue}, Price: ${nativePrice}, Total: ${nativeTotal}`
            );
  
            return {
              chain,
              native: nativeValue,
              nativePrice,
              tokenTotal: 0,
              nativeTotal,
              totalBalance: nativeTotal, // No token logic for Rails-specific chain
            };
          } catch (error) {
            console.error(`Error fetching Rails balances:`, error);
            return { chain, native: 0, nativePrice: 0, tokenTotal: 0, nativeTotal: 0, totalBalance: 0 };
          }
        } else {
          // Logic for other chains
          try {
            const provider = new ethers.JsonRpcProvider(chain.rpcUrl);
            const nativeBalance = await provider.getBalance(address);
            const nativeValue = parseFloat(ethers.formatEther(nativeBalance)) || 0;
  
            // Fetch native token price
            let nativePrice = 0;
            if (chain.priceEndpoint) {
              try {
                const response = await fetch(chain.priceEndpoint, { method: "GET" });
                if (response.ok) {
                  const data = await response.json();
                  nativePrice = parseFloat(data?.data?.attributes?.price_usd) || 0;
                } else {
                  console.warn(`Failed to fetch price for ${chain.name}`);
                }
              } catch (error) {
                console.error(`Error fetching native token price for ${chain.name}:`, error);
              }
            }
  
            const nativeTotal = nativeValue * nativePrice;
            console.log(
              `Native Token for ${chain.name} - Balance: ${nativeValue}, Price: ${nativePrice}, Total: ${nativeTotal}`
            );
  
            // Fetch other tokens from Moralis
            const moralisChain = chain.chainKey?.toLowerCase();
            const apiUrl = `https://deep-index.moralis.io/api/v2.2/wallets/${address}/tokens?chain=${moralisChain}`;
            console.log(`Fetching tokens from: ${apiUrl}`);
  
            const response = await fetch(apiUrl, {
              method: "GET",
              headers: {
                "x-api-key": moralisApiKey,
                accept: "application/json",
              },
            });
  
            if (!response.ok) {
              throw new Error(`Error fetching tokens for ${chain.name}: ${response.statusText}`);
            }
  
            const data = await response.json();
            const filteredTokens = (data.result || []).filter((token) => token.logo && token.token_address);
  
            const tokenTotal = await Promise.all(
              filteredTokens.map(async (token) => {
                const balance = parseFloat(ethers.formatUnits(token.balance, token.decimals));
                const price = await fetchTokenPrice(chain.chainKey.toLowerCase(), token.token_address);
                console.log(
                  `Token: ${token.symbol}, Balance: ${balance}, Price: ${price}, Value: ${balance * price}`
                );
                return balance * price;
              })
            ).then((values) => values.reduce((sum, value) => sum + value, 0));
  
            const totalBalance = nativeTotal + tokenTotal;
  
            return {
              chain,
              native: nativeValue,
              nativePrice,
              tokenTotal,
              nativeTotal,
              totalBalance,
            };
          } catch (error) {
            console.error(`Error fetching balances for ${chain.name}:`, error);
            return { chain, native: 0, nativePrice: 0, tokenTotal: 0, nativeTotal: 0, totalBalance: 0 };
          }
        }
      })
    );
  
    setNetworkBalances(balances);
  };
  
  const disconnectWallet = () => {
    setWalletAddress("");
    setNetworkBalances([]);
    if (typeof toggleWalletModal === "function") toggleWalletModal();
  };

  const getButtonLabel = () => {
    if (!walletAddress) {
      return "Connect Wallet";
    } else if (isExpanded) {
      return "Close";
    } else {
      return `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`;
    }
  };
  
  return (
<div
  className={`fixed top-5 right-5 bg-gray-800 text-white rounded-lg shadow-lg transition-all duration-300 ${zIndex} ${
    isExpanded
      ? "w-[90vw] h-[90vh] lg:w-96 lg:h-auto p-4 overflow-y-auto"
      : "w-36 h-16 flex items-center justify-center"
  }`}
  style={{
    maxWidth: "400px", // Ensures it doesn't exceed on large screens
    maxHeight: "90vh", // Makes it scrollable on smaller screens
  }}
>
  {/* Connect Button */}
  <button
    onClick={walletAddress ? toggleWalletModal : connectWallet}
    className="flex items-center justify-center w-full h-16 text-lg font-semibold text-white transition-all bg-yellow-500 rounded hover:bg-yellow-600"
    style={{ boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)" }}
  >
    {getButtonLabel()}
  </button>

  
      {isExpanded && walletAddress && (
        <div className="flex flex-col mt-4 space-y-4">
          <h3 className="text-lg font-semibold">Networks:</h3>
          {networkBalances.map((network) => {
            const { native, nativePrice, tokenTotal, nativeTotal } = network;
  
            return (
              <div
                key={network.chain.id}
                className="flex flex-col px-4 py-2 space-y-2 bg-gray-700 rounded"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={network.chain.image}
                    alt={network.chain.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <span className="text-sm font-medium">{network.chain.name}</span>
                </div>
                <div className="text-sm">
                  <div>
                    <strong>Native:</strong> {native.toFixed(6)} {network.chain.symbol} ($
                    {(native * nativePrice).toFixed(2)})
                  </div>
                  <div>
                    <strong>Tokens:</strong> ${tokenTotal.toFixed(2)}
                  </div>
                  <div>
                    <strong>Total:</strong> ${(nativeTotal + tokenTotal).toFixed(2)}
                  </div>
                </div>
              </div>
            );
          })}
          <button
            onClick={disconnectWallet}
            className="w-full px-4 py-2 font-semibold text-white transition bg-red-500 rounded hover:bg-red-600"
          >
            Disconnect
          </button>
        </div>
      )}
    </div>
  );
  
}  

export default WalletConnectButton;
